import Skeleton from 'react-loading-skeleton';

import { Checkbox } from '../../../../cdl/Checkbox/Checkbox';
import { Switch } from '../../../../cdl/Switch/Switch';
import { Text } from '../../../../cdl/Text/Text';
import { GenericError } from '../../../../common/Error/GenericError';
import { formatCompanyName } from '../../../../common/helpers/formatCompanyName.helper';
import { translate } from '../../../../common/helpers/translate.helper';
import { Box } from '../../../../common/ui/Box';
import { useCustomerGroupSettings } from '../hooks/useCustomerGroupSettings';
import { useCustomerGroupSettingsOptimisticMutation } from '../hooks/useCustomerGroupSettingsOptimisticMutation';
import { useCustomerList } from '../hooks/useCustomerList';

interface ScheduleSharingSettingsProps {
    groupId: string;
}

export const ScheduleSharingSettings = ({ groupId }: ScheduleSharingSettingsProps) => {
    const customerGroupSettingsQuery = useCustomerGroupSettings(groupId);
    const mutation = useCustomerGroupSettingsOptimisticMutation(groupId);
    const customerListQuery = useCustomerList({ groupId });

    const handleToggleScheduleSharing = () => {
        if (customerGroupSettingsQuery.data) {
            mutation.toggleScheduleSharing(customerGroupSettingsQuery.data);
        }
    };

    const handleToggleExcludeCompanies = () => {
        if (customerGroupSettingsQuery.data) {
            mutation.toggleExcludeEnabled(customerGroupSettingsQuery.data);
        }
    };

    const handleToggleCompanyExclusion = (companyId: string) => {
        if (!customerGroupSettingsQuery.data) return;

        const currentExcluded = customerGroupSettingsQuery.data.scheduleSharingExcludedCompanyIds ?? [];
        const updatedExcluded = currentExcluded.includes(companyId)
            ? currentExcluded.filter((id) => id !== companyId)
            : [...currentExcluded, companyId];

        mutation.updateExcludedCompanyIds(customerGroupSettingsQuery.data, updatedExcluded);
    };

    return (
        <Box display="flex" flexDirection="column" gap={16}>
            <Text variant="subtitle1" as="h2">
                {translate('customers.supplierPermissionsHeadline')}
            </Text>

            {customerGroupSettingsQuery.isPending ? <Skeleton height="40px" width="70%" /> : null}
            {customerGroupSettingsQuery.isError ? <GenericError /> : null}
            {customerGroupSettingsQuery.data ? (
                <>
                    <Switch
                        onChange={handleToggleScheduleSharing}
                        checked={customerGroupSettingsQuery.data.scheduleSharingEnabled}
                        label={translate('customers.scheduleSharingSwitchLabel')}
                        description={translate('customers.scheduleSharingDescription')}
                    />

                    {customerGroupSettingsQuery.data.scheduleSharingEnabled ? (
                        <Box display="flex" flexDirection="column" gap={12} paddingLeft={32}>
                            <Switch
                                onChange={handleToggleExcludeCompanies}
                                checked={customerGroupSettingsQuery.data.scheduleSharingExcludeEnabled}
                                label={translate('customers.excludeCompaniesSwitchLabel')}
                            />

                            {customerGroupSettingsQuery.data.scheduleSharingExcludeEnabled ? (
                                <Box display="flex" flexDirection="column" gap={8} paddingLeft={32}>
                                    <Text variant="fieldLabel">{translate('customers.excludeCompaniesHeadline')}</Text>
                                    {customerListQuery.data?.map((company) => (
                                        <Checkbox
                                            key={company.id}
                                            checked={
                                                customerGroupSettingsQuery.data?.scheduleSharingExcludedCompanyIds?.includes(
                                                    company.id
                                                ) ?? false
                                            }
                                            onChange={() => handleToggleCompanyExclusion(company.id)}
                                            label={formatCompanyName({ company })}
                                        />
                                    ))}
                                </Box>
                            ) : null}
                        </Box>
                    ) : null}
                </>
            ) : null}
        </Box>
    );
};
